/**
 * Trunnion AI - Cost Trend Chart
 * Area chart of token costs over time with period selector.
 */

import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DollarSign } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useTokenUsageSummary } from '@/hooks/queries/useDashboard';

type Period = 'day' | 'week' | 'month';

const periodOptions: { label: string; value: Period }[] = [
  { label: '7d', value: 'day' },
  { label: '30d', value: 'week' },
  { label: '90d', value: 'month' },
];

interface CostDataPoint {
  date: string;
  cost: number;
}

export function CostTrendChart() {
  const [period, setPeriod] = useState<Period>('week');
  const { data, isLoading } = useTokenUsageSummary(period);

  const chartData: CostDataPoint[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    // Aggregate cost across all providers by date
    const dateMap = new Map<string, number>();
    for (const summary of data) {
      for (const point of summary.breakdown) {
        const existing = dateMap.get(point.date) ?? 0;
        dateMap.set(point.date, existing + point.cost);
      }
    }
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, cost]) => ({ date, cost }));
  }, [data]);

  return (
    <Card padding="none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <DollarSign className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
            Cost Trends
          </CardTitle>
          <div className="flex items-center gap-1">
            {periodOptions.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => setPeriod(value)}
                className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                  period === value
                    ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500'
                    : 'text-trunnion-slate-400 hover:text-trunnion-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : chartData.length === 0 ? (
          <p className="text-sm text-trunnion-slate-400 text-center py-4">
            No cost data available
          </p>
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4A017" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#D4A017" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#8D97A6', fontSize: 10 }}
                  axisLine={{ stroke: '#374151' }}
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  }
                />
                <YAxis
                  tick={{ fill: '#8D97A6', fontSize: 10 }}
                  axisLine={{ stroke: '#374151' }}
                  width={50}
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A202C',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  labelStyle={{ color: '#FFFFFF' }}
                  labelFormatter={(label: string) =>
                    new Date(label).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  }
                  formatter={(value: number) => [`$${value.toFixed(4)}`, 'Cost']}
                />
                <Area
                  type="monotone"
                  dataKey="cost"
                  name="Cost"
                  stroke="#D4A017"
                  strokeWidth={2}
                  fill="url(#costGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
